import { useState } from 'react'
import { Check, Copy, QrCode, Share2, ShieldCheck, X } from 'lucide-react'
import { useLang, t, fmt } from '../lib/i18n'

/* ============================================================
   Portable proof — SMART Health Card (SHC) + SMART Health Link (SHL)
   ============================================================
   Static demo artifacts generated by apps/api/app/scripts/seed_shc_demo.py
   (see docs/design/smart-health-cards-and-links.md §5). The QR PNGs live in
   apps/web/public/demo/ and are served as static assets. When the SHC/SHL API
   is running, these can be swapped for live endpoint responses; for now they
   are the committed demo artifacts (no API required).
   ============================================================ */

const DEMO_SH_CARD_QR = '/demo/shc-covid-1.png'
const DEMO_SHL_QR = '/demo/shl-vaccines.png'

// The SHL descriptor is a `shlink:/` payload. The demo link is passcode-protected
// (dev/seed mode only — see ADR-016). This is the human-shareable form.
const DEMO_SHL_LINK = 'shlink:/eyJ1cmwiOiJodHRwczovL3N5cmlhcGhyLnBhZ2VzLmRldi9hcGkvdjEvc2hsL21hbmlmZXN0L2RlbW8tdmFjY2luZXMiLCJrZXkiOiJYMnhZTUJpYjhQTFBoMGsyT1JyVTRRLXFLNzAwZ0RBRlR6aUZFQk04aW9ZIiwiZmxhZyI6IlAiLCJsYWJlbCI6IlN5cmlhUEhSIGRlbW8gdmFjY2luZXMifQ'

const DEMO_SHL_PASSCODE = '123456'

type ProofModal = 'shc' | 'shl' | null

export function PortableProof() {
  const [modal, setModal] = useState<ProofModal>(null)
  const [copied, setCopied] = useState<'shc' | 'shl' | null>(null)

  function copyShlLink() {
    navigator.clipboard?.writeText(DEMO_SHL_LINK).catch(() => {})
    setCopied('shl')
    window.setTimeout(() => setCopied(null), 2000)
  }

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft" aria-label={t('proof.aria')}>
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary-700" />
        <h2 className="text-lg font-semibold text-stone-950">{t('proof.title')}</h2>
        <span className="ml-auto hidden text-xs text-stone-500 sm:block">{t('proof.std')}</span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* SMART Health Card (COVID) */}
        <div className="flex items-start gap-4 rounded-lg border border-stone-200 bg-stone-50/60 p-4">
          <button
            type="button"
            onClick={() => setModal('shc')}
            aria-label={t('proof.shcAria')}
            className="shrink-0 rounded-lg border border-stone-200 bg-white p-2 shadow-soft transition hover:border-emerald-200 hover:shadow-md active:scale-95"
          >
            <img src={DEMO_SH_CARD_QR} alt={t('proof.shcImg')} className="h-24 w-24" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
              <QrCode className="h-4 w-4 text-emerald-700" />
              {t('proof.shc')}
            </div>
            <p className="mt-1 text-xs leading-5 text-stone-600">
              {t('proof.shcDesc')}
            </p>
            <button
              type="button"
              onClick={() => setModal('shc')}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 transition hover:text-emerald-900"
            >
              {t('proof.viewQr')}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>

        {/* SMART Health Link (all vaccines) */}
        <div className="flex items-start gap-4 rounded-lg border border-stone-200 bg-stone-50/60 p-4">
          <button
            type="button"
            onClick={() => setModal('shl')}
            aria-label={t('proof.shlAria')}
            className="shrink-0 rounded-lg border border-stone-200 bg-white p-2 shadow-soft transition hover:border-emerald-200 hover:shadow-md active:scale-95"
          >
            <img src={DEMO_SHL_QR} alt={t('proof.shlImg')} className="h-24 w-24" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-stone-900">
              <Share2 className="h-4 w-4 text-emerald-700" />
              {t('proof.shl')}
            </div>
            <p className="mt-1 text-xs leading-5 text-stone-600">
              {t('proof.shlDesc')}
            </p>
            <button
              type="button"
              onClick={() => setModal('shl')}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 transition hover:text-emerald-900"
            >
              {t('proof.viewQr')}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>

      {modal && (
        <ProofModal
          kind={modal}
          onClose={() => setModal(null)}
          copied={copied}
          onCopyShl={copyShlLink}
        />
      )}
    </section>
  )
}

function ProofModal({
  kind,
  onClose,
  copied,
  onCopyShl,
}: {
  kind: 'shc' | 'shl'
  onClose: () => void
  copied: 'shc' | 'shl' | null
  onCopyShl: () => void
}) {
  useLang()
  const isShc = kind === 'shc'
  const title = isShc ? t('proof.shc') : t('proof.shl')
  const qr = isShc ? DEMO_SH_CARD_QR : DEMO_SHL_QR
  const alt = isShc ? t('proof.shcImg') : t('proof.shlImg')

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-stone-950/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="proof-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm animate-pop-in rounded-2xl border border-stone-200 bg-white p-6 shadow-lift-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 id="proof-modal-title" className="text-lg font-semibold text-stone-950">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('proof.close')}
            className="grid h-9 w-9 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:bg-stone-50 active:scale-90"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mx-auto w-fit rounded-xl border border-stone-200 bg-white p-3 shadow-soft">
          <img src={qr} alt={alt} className="h-56 w-56" />
        </div>

        {isShc ? (
          <p className="mt-4 text-center text-xs leading-5 text-stone-500">
            {t('proof.shcModalHint')}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <div className="mb-1 text-xs font-semibold text-stone-500">{t('proof.shareLink')}</div>
              <div className="flex items-center gap-2">
                <code className="ltr-keep min-w-0 flex-1 truncate rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs text-emerald-800">
                  {DEMO_SHL_LINK}
                </code>
                <button
                  type="button"
                  onClick={onCopyShl}
                  aria-label={t('proof.copyLinkAria')}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:border-emerald-200 hover:text-emerald-800 active:scale-90"
                >
                  {copied === 'shl' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <p className="text-xs leading-5 text-stone-500">
              {fmt(t('proof.shlModalHint'), { code: DEMO_SHL_PASSCODE })}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
